// libs
import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

// Components
import DocumentMeta from 'core/components/DocumentMeta'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import Progress from 'core/components/progress/Progress'
import Button from 'core/elements/button/Button'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import useListAction from 'core/hooks/useListAction'
import { listClusterHostGroups, listClusterVMHAStatus } from '../actions'
import { clusterHostGroupsSelector, clusterVMHAStatusSelector } from '../selectors'
import ClusterHostGroupOverview from './ClusterHostGroupOverview'
import StatusCard from 'core/components/statusCard/StatusCard'
import StatusIconText from 'k8s/components/dashboard/StatusIconText'
import { IClusterHostGroup } from '../model'
import { generateHostStatus, mapVMHAConditions } from '../helpers'
import HostsListPage from '../../hosts/HostsListPage'
import { checkVMHAStatusForCluster } from '../../actions'
import ClusterHostGroupVmhaStatusDetails from './ClusterHostGroupVmhaStatusDetails'
import EditClusterHostGroupModal from '../EditClusterHostGroupModal'

const ClusterDetails = () => {
  const { match, history } = useReactRouter()
  const { id } = match.params
  const [vmhaClusterDetails, setVmhaClusterDetails] = useState()
  const [vmhaDisabled, setVmhaDisabled] = useState(false)
  const [vmhaClusterStatusLoading, setVmhaClusterStatusLoading] = useState(false)
  const [openEnableVmhaModal, setOpenEnableVmhaModal] = useState(false)

  // Actions
  const { loading, reload } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const clusterHostGroup: IClusterHostGroup = clusterHostGroups.find(
    (clusterHostGroup) => clusterHostGroup.name === id,
  )
  const clusterHostsStatus = useMemo(() => {
    return generateHostStatus(clusterHostGroup?.hosts)
  }, [clusterHostGroup])

  const isVMHAEnabledForAnyCluster = useMemo(
    () => clusterHostGroups.some((cluster) => cluster?.vmHighAvailability?.enabled),
    [clusterHostGroups],
  )

  const { loading: loadingClusterVMHAStatus, reload: loadClusterVMHAStatus } = useListAction(
    listClusterVMHAStatus,
    {
      loadOnDemand: true,
      params: { clusters: clusterHostGroups },
    },
  )
  const clusterVMHAStatus = useSelector(clusterVMHAStatusSelector)

  // Load Cluster VMHA Status
  useEffect(() => {
    if (
      clusterHostGroups?.length === 0 ||
      clusterVMHAStatus?.length > 0 ||
      !isVMHAEnabledForAnyCluster ||
      loading ||
      loadingClusterVMHAStatus
    ) {
      return
    }

    loadClusterVMHAStatus(true, true)
  }, [
    clusterHostGroups,
    isVMHAEnabledForAnyCluster,
    loading,
    loadingClusterVMHAStatus,
    clusterVMHAStatus,
  ])

  const classes = useStyles({ containsHosts: clusterHostGroup?.hosts?.length > 0 })

  const fetchVmhaClusterStatus = useCallback(async () => {
    if (!id) return

    try {
      setVmhaClusterStatusLoading(true)

      const configs: any = await checkVMHAStatusForCluster(id)

      const conditions = mapVMHAConditions(configs)
      const hasFailure = conditions?.some((c) => !c.passed)

      setVmhaDisabled(hasFailure)
      setVmhaClusterDetails(configs)
    } catch (error) {
      console.error(error)
    } finally {
      setVmhaClusterStatusLoading(false)
    }
  }, [id])

  const enableVmhaCallback = useCallback(() => {
    setOpenEnableVmhaModal(true)
  }, [id])

  useEffect(() => {
    fetchVmhaClusterStatus()
  }, [fetchVmhaClusterStatus])

  return (
    <>
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
            loadClusterVMHAStatus(true, true)
          }}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      {openEnableVmhaModal && (
        <EditClusterHostGroupModal
          onClose={() => setOpenEnableVmhaModal(false)}
          rows={[clusterHostGroup]}
          isVmhaModalView
        />
      )}
      <Progress loading={loading}>
        <DocumentMeta title="Dashboard" />
        {clusterHostGroup ? (
          <>
            <div className={classes.customGridContainer}>
              <div className={classes.customGridItem}>
                <StatusCard show={true} title={`Hosts (${clusterHostGroup.hosts.length})`}>
                  {clusterHostsStatus?.map((item, index) => (
                    <StatusIconText
                      key={index}
                      label={item?.label}
                      icon={item?.icon}
                      variant={item?.variant}
                      rightBorder={index !== clusterHostsStatus?.length - 1}
                      margin={false}
                    />
                  ))}
                </StatusCard>
                <ClusterHostGroupOverview clusterHostGroup={clusterHostGroup} />
                <ClusterHostGroupVmhaStatusDetails
                  vmhaClusterDetails={vmhaClusterDetails}
                  loading={vmhaClusterStatusLoading}
                  onClickRefresh={fetchVmhaClusterStatus}
                  clusterHostGroup={clusterHostGroup}
                  vmhaDisabled={vmhaDisabled}
                  onClickEnableVmha={enableVmhaCallback}
                />
              </div>
              <div className={classes.hostsList}>
                <HostsListPage
                  renderFromClustersPage={true}
                  hostlist={clusterHostGroup?.hostlist}
                />
              </div>
            </div>
          </>
        ) : (
          <Text variant="body1">No Clusters found</Text>
        )}
      </Progress>
    </>
  )
}

export default ClusterDetails

const useStyles = makeStyles<Theme, { containsHosts: boolean }>((theme: Theme) => ({
  customGridContainer: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: ({ containsHosts }) => (containsHosts ? '370px auto' : '370px 1fr'),
    gridGap: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  customGridItem: {
    display: 'grid',
    gridGap: theme.spacing(3),
    gridTemplateRows: '112px max-content',
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  headerSubText: {
    paddingTop: theme.spacing(1),
  },
  divider: {
    border: '0',
    height: '1px',
    margin: theme.spacing(1, 0),
    background: '#e6e6ea',
  },
  currentVersion: {
    display: 'flex',
    alignItems: 'center',
  },
  hostsList: {
    '& .progress-root': {
      height: 'auto',
    },
  },
}))
